import cloneDeep from 'lodash.clonedeep';

import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { incentiveUtils } from '@makemydeal/dr-common-utils';
import { CORRELATION_ID_HEADER } from '@makemydeal/dr-platform-shared';
import type {
    GenericObject,
    IPaymentOverride,
    OfferType,
    PaymentScheduleTypes,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';
import { ApplicationCodes, BALLOON, CASH, FINANCE, LEASE, OFFER_ENCRYPTION_FIELDS } from '@makemydeal/dr-platform-types';

import {
    getAutoTermId,
    getCurrentOfferType,
    getDownPayment,
    getOfferDetails,
    getFeatureToggle,
    getFlexBuyPaymentGridToggle,
    getIsDueAtSigninAmountDeduct,
    getIsSalesView,
    getOfferInitialPaymentDiscount,
    getSelectedTermMonths,
    isFlexBuyPayment,
    shouldHonorDealerStandardExclusion
} from '../selectors';
import { getActionForPayment, getTraceId } from '../selectors/paymentSelectors';
import type { FlexBuyPaymentGridDataItem, UpdateFlexBuyPaymentGridDataPayload } from '../types/flexBuyTypes';
import type { HostSelectors, IPaymentConfig, PaymentReduxAction, TransformedOfferPaymentData } from '../types/paymentTypes';
import { PAYMENT_FAILURE_TIMEOUT, PAYMENT_REQUEST_OVERRIDE_NODE, REQUEST_LEDGER_REBATES_META } from '../constants/payment';
import type { AutoTermIdParams } from '../types/commonTypes';
import { getDefaultTerms } from '../utils';
import {
    isGridCashDownCatalogCarryEnabled,
    isGridRebateIdsConsistencyEnabled,
    isProgramIncentivesRestorationEnabled,
    sumLedgerRebates
} from '../utils/incentives';
import { isRegularGridRowAction } from '../utils/paymentGridUtils';
import { fetchPayments, isRequestAborted, toFetchPaymentError } from '../utils/paymentFetcher';
import type { FetchPaymentResult } from '../utils/paymentFetcher';
import {
    createPaymentPayload,
    GRID_ASK_TURNED_ON_INCENTIVES_META,
    getActionMetaValue,
    getPreferredPaymentTermMonths,
    mapTermsForOffer,
    setActionMetaValue,
    transformPaymentResponse
} from '../utils/paymentPayloadUtils';
import { EXPLICIT_PROGRAM_LOCK_META } from '../utils/explicitProgramLockUtils';
import { getNegativeLeaseQuoteError, resolveRequestCalculationEndpointFromAction, withMeta } from '../utils/paymentUtils';
import * as actionTypes from './paymentActionTypes';
import { ZIP_CODE_PAYMENT_FAILED } from './paymentActionTypes';
import { APPLY_DEALER_PAYMENT_OVERRIDES } from '../types/actionTypes';
import type { AnyDispatch } from '@makemydeal/dr-browser-utils/typedefs/types/reduxTypes';

export const paymentFailureMappings = {
    FETCH_QUESTIONS_OR_PAYMENT: ZIP_CODE_PAYMENT_FAILED,
    UPDATED_TRADE_IN: actionTypes.TRADE_IN_PAYMENT_FAILED,
    UPDATE_SELECTED_INCENTIVES: actionTypes.INCENTIVES_PAYMENT_FAILED,
    SELECTED_CREDIT_TIER: actionTypes.CREDIT_TIER_PAYMENT_FAILED,
    UPDATED_OFFER_PRICE_DEBOUNCED: actionTypes.OFFER_PRICE_PAYMENT_FAILED,
    UPDATED_DOWN_PAYMENT_DEBOUNCED: actionTypes.DOWN_PAYMENT_PAYMENT_FAILED,
    SELECTED_ANNUAL_MILES: actionTypes.ANNUAL_MILES_PAYMENT_FAILED
};

const errorPayment = {
    finance: {
        success: false,
        incentives: [] as any[],
        selectedTermMonths: null as any
    },
    lease: {
        success: false,
        incentives: [] as any[],
        selectedTermMonths: null as any
    },
    terms: getDefaultTerms(),
    incentives: {
        allIds: [] as any[],
        byId: [] as any[],
        byOfferType: [] as any[],
        mappings: [] as any[]
    },
    traceId: '',
    actionForPayment: '',
    zipCodePaymentFailed: false
};

// Used for taxes/fees payments -- retrieves questions or taxes/fees payment
// Typically used by zip updaters to check for questions
export const getPayment = (zipCode: any, applyTaxesAndFees: any): PaymentReduxAction => {
    // If there is no zip code, get a payment without taxes and fees
    if (!applyTaxesAndFees || !zipCode) {
        return {
            type: actionTypes.PAYMENT_REQUESTED,
            payload: {
                includeTaxes: false,
                zipCode: ''
            },
            meta: {
                middleware: {
                    payment: true
                }
            }
        };
    }
    // Otherwise, retrieve a payment with taxes and fees
    return {
        type: actionTypes.FETCH_QUESTIONS_OR_PAYMENT,
        payload: {
            answers: {},
            zipCode
        },
        meta: {
            middleware: {
                payment: true
            }
        }
    };
};

export const updateLocationId = (locationId: any): PaymentReduxAction => {
    return {
        type: actionTypes.LOCATION_ID_UPDATED,
        payload: {
            selectedLocationId: locationId
        },
        meta: {
            middleware: {
                payment: true
            }
        }
    };
};

export const updatePaymentAnswers = (answers: string): PaymentReduxAction => {
    return {
        type: actionTypes.PAYMENT_ANSWERS_UPDATED,
        payload: {
            answers
        },
        meta: {
            middleware: {
                payment: true
            }
        }
    };
};

type CompatibilityPayment = {
    compatibleIncentiveIds?: number[];
    term?: number;
    creditTier?: number;
    lenderProgram?: { lenderId?: number; programName?: string };
};

const hasCompatibleIds = (payment: CompatibilityPayment): boolean =>
    Array.isArray(payment.compatibleIncentiveIds) && payment.compatibleIncentiveIds.length > 0;

const isSameAppliedProgram = (payment: CompatibilityPayment, appliedProgram: CompatibilityPayment): boolean =>
    payment.term === appliedProgram.term &&
    payment.creditTier === appliedProgram.creditTier &&
    payment.lenderProgram?.lenderId === appliedProgram.lenderProgram?.lenderId &&
    payment.lenderProgram?.programName === appliedProgram.lenderProgram?.programName;

const getAppliedProgramCompatibleIncentiveIds = (
    payments: CompatibilityPayment[] | undefined,
    appliedProgram: CompatibilityPayment | undefined
): number[] | undefined => {
    if (!Array.isArray(payments)) return undefined;
    const matched =
        appliedProgram != null
            ? payments.find((payment) => isSameAppliedProgram(payment, appliedProgram) && hasCompatibleIds(payment))
            : payments.find(hasCompatibleIds);
    return matched ? matched.compatibleIncentiveIds : undefined;
};

const getSelectedTermCompatibleIncentiveIds = (
    payments: CompatibilityPayment[] | undefined,
    selectedTermMonths: number | undefined
): number[] | undefined =>
    Array.isArray(payments)
        ? payments.find((payment) => payment.term === selectedTermMonths && hasCompatibleIds(payment))?.compatibleIncentiveIds
        : undefined;

const isGridSubPayment = (action: PaymentReduxAction): boolean => {
    const paymentGrid = (action as AnyFSA)?.meta?.paymentGrid;
    return Boolean(paymentGrid?.gridAction) && !paymentGrid?.isMainPayment;
};

type RawProgramIncentivesMeta = { rawProgramIncentives?: unknown; appliedProgramCompatibleIncentiveIds?: number[] };

const buildRawProgramIncentivesMeta = (
    result: any,
    state: any,
    action: PaymentReduxAction,
    offerType: OfferType,
    newOffer: TransformedOfferPaymentData
): RawProgramIncentivesMeta | undefined => {
    if (action?.type === 'APPLIED_PROGRAM') {
        return {
            rawProgramIncentives: result?.[offerType]?.incentives,
            appliedProgramCompatibleIncentiveIds: getAppliedProgramCompatibleIncentiveIds(
                result?.[offerType]?.payments,
                (action as AnyFSA)?.payload?.programData
            )
        };
    }
    if (
        offerType === CASH ||
        !isGridCashDownCatalogCarryEnabled(state) ||
        !isProgramIncentivesRestorationEnabled(state) ||
        isGridSubPayment(action) ||
        offerType !== getCurrentOfferType(state)
    ) {
        return undefined;
    }
    const offerResult = result?.[offerType];
    if (offerResult?.incentives == null) {
        return undefined;
    }
    return {
        rawProgramIncentives: offerResult.incentives,
        appliedProgramCompatibleIncentiveIds: getSelectedTermCompatibleIncentiveIds(
            offerResult.payments,
            (newOffer as GenericObject)[offerType]?.selectedTermMonths
        )
    };
};

export const paymentFailed = (action: AnyFSA): AnyFSA => {
    const failureActionType = (paymentFailureMappings as Record<string, string>)[action.type] || `${action.type}_PAYMENT_FAILED`;
    return {
        type: failureActionType
    };
};

const didServiceSeedStandards = (payload: GenericObject, offerType?: OfferType): boolean =>
    (payload?.payment as GenericObject | undefined)?.[offerType as string]?.excludeStdIncs === false;

const didRequestExcludeDealerStandards = (
    payload: GenericObject,
    state: any,
    offerType: OfferType,
    hostSelectors?: HostSelectors
): boolean =>
    !hostSelectors?.isManagerView &&
    shouldHonorDealerStandardExclusion(state, offerType) &&
    (payload.payment as GenericObject | undefined)?.[offerType]?.excludeStdIncs === true;

export type BuildOfferFromPaymentResultParams = {
    result: any;
    state: any;
    action: PaymentReduxAction;
    offerType: OfferType;
    sourceApplication?: string;
};

export const buildOfferFromPaymentResult = ({
    result,
    state,
    action,
    offerType,
    sourceApplication
}: BuildOfferFromPaymentResultParams): TransformedOfferPaymentData => {
    const useNewTermIdStandard = getFeatureToggle(state, 'useNewTermIdStandard');

    const offer = transformPaymentResponse(result, offerType, action.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]);
    const newOffer = {} as TransformedOfferPaymentData;

    let currentOffer: GenericObject;
    // TODO: Type of offerPayments depends on the type of 'result' object which is a response of a 'fetch' call, need more info to type it properly
    let offerPayments: any[];
    if (result?.[OFFER_ENCRYPTION_FIELDS.PAYMENT]) {
        newOffer[OFFER_ENCRYPTION_FIELDS.PAYMENT] = result[OFFER_ENCRYPTION_FIELDS.PAYMENT];
    }

    if (result?.questions) {
        newOffer.questions = result.questions;
    }

    if (offerType === CASH) {
        newOffer[offerType] = {
            ...offer[offerType]
        };
        newOffer.incentives = incentiveUtils.mapIncentivesForOffer(cloneDeep(offer));
    } else {
        currentOffer = offer[offerType];
        offerPayments = currentOffer.payments || [];
        const selectedTermMonths = getPreferredPaymentTermMonths(offerPayments) || 0;

        newOffer[offerType] = {
            success: currentOffer.success,
            incentives: currentOffer.incentives,
            selectedTermMonths,
            isRequestedTermInPayments: (currentOffer as any).isRequestedTermInPayments,
            firstPaymentCapitalized: offerPayments[0]?.firstPaymentCapitalized,
            cashOption: offerPayments[0]?.cashOption,
            netSellingPrice: offerPayments[0]?.netSellingPrice,
            estTotalPurchasePrice: offerPayments[0]?.estTotalPurchasePrice,
            registrationType: offerPayments[0]?.registrationType,
            registrationExpiresOn: offerPayments[0]?.registrationExpiresOn
        } as any;

        if (offerType === LEASE && offerPayments.length > 0) {
            const firstPayment = offerPayments[0] as any;
            (newOffer[offerType] as any).equityAllocationMethod = firstPayment?.equityAllocationMethod;
            (newOffer[offerType] as any).vehicleSalePrice = firstPayment.vehicleSalePrice;
            (newOffer[offerType] as any).cashOffsetAmountAddedToPrice = firstPayment.cashOffsetAmountAddedToPrice;
            (newOffer[offerType] as any).tradeApplyMethod = firstPayment?.tradeApplyMethod;
            (newOffer[offerType] as any).rebateApplyMethod = firstPayment?.rebateApplyMethod;
            (newOffer[offerType] as any).dealerCashApplyMethod = firstPayment?.dealerCashApplyMethod;
            (newOffer[offerType] as any).cashApplyMethod = firstPayment?.cashApplyMethod ?? firstPayment?.cashOption;
            (newOffer[offerType] as any).retroMiles = firstPayment?.retroMiles;
            (newOffer[offerType] as any).thresholdMiles = firstPayment?.thresholdMiles;
            (newOffer[offerType] as any).inceptionRate = firstPayment?.inceptionRate;
            (newOffer[offerType] as any).initialMilesCharged = firstPayment?.initialMilesCharged;
            (newOffer[offerType] as any).initialMileageCharge = firstPayment?.initialMileageCharge;
            (newOffer[offerType] as any).standardAllowedMiles = firstPayment?.standardAllowedMiles;
            (newOffer[offerType] as any).excessMileRate = firstPayment?.excessMileRate;
            (newOffer[offerType] as any).totalExcessMilesPurchased = firstPayment?.totalExcessMilesPurchased;
            (newOffer[offerType] as any).excessAmount = firstPayment?.excessAmount;
            (newOffer[offerType] as any).totalMiles = firstPayment?.totalMiles;
            (newOffer[offerType] as any).excessMilesCharge = firstPayment?.excessMilesCharge;
            (newOffer[offerType] as any).secondPaymentDueDate = firstPayment?.secondPaymentDueDate;
            (newOffer[offerType] as any).excessMileageCharge = firstPayment?.excessMileageCharge;
            (newOffer[offerType] as any).retroMilesThreshold = firstPayment?.retroMilesThreshold;
            (newOffer[offerType] as any).initialMilesThreshold = firstPayment?.initialMilesThreshold;
            (newOffer[offerType] as any).standardMiles = firstPayment?.standardMiles;
            (newOffer[offerType] as any).paymentFrequency = firstPayment?.paymentFrequency;
            (newOffer[offerType] as any).isTargetCashAmountPaid = firstPayment?.isTargetCashAmountPaid;
        }

        // Prior balance is supported on finance, lease and balloon, and the Orchestrator sends it without
        // deal-type filtering, so record what Payment Services applied on whichever type was priced.
        if (typeof (offerPayments[0] as any)?.priorLeaseBalance === 'number') {
            const firstPayment = offerPayments[0] as any;
            (newOffer[offerType] as any).priorLeaseBalance = firstPayment.priorLeaseBalance;
            (newOffer[offerType] as any).priorLeaseBalanceMethod = firstPayment.priorLeaseBalanceMethod;
        }

        // Lease has always had these on the payment root; the Orchestrator now sends them on finance too.
        if (offerType !== LEASE && typeof (offerPayments[0] as any)?.vehicleSalePrice === 'number') {
            const firstPayment = offerPayments[0] as any;
            (newOffer[offerType] as any).vehicleSalePrice = firstPayment.vehicleSalePrice;
            (newOffer[offerType] as any).cashOffsetAmountAddedToPrice = firstPayment.cashOffsetAmountAddedToPrice;
        }

        if (offerType === BALLOON && offerPayments.length > 0) {
            const firstPayment = offerPayments[0] as any;
            (newOffer[offerType] as any).vehicleSalePrice = firstPayment?.vehicleSalePrice;
            (newOffer[offerType] as any).cashOffsetAmountAddedToPrice = firstPayment?.cashOffsetAmountAddedToPrice;
        }

        populateFlexBuyPaymentData({ state, currentOfferType: offerType, offerPayments, newOffer, action });

        const autoTermId =
            sourceApplication === ApplicationCodes.MV && useNewTermIdStandard
                ? (termIdParams: AutoTermIdParams, getPairs: boolean): string =>
                      getAutoTermId(state, termIdParams.months, {
                          // Key the stored term records by THIS payment's offer type, not the deal's
                          // current offer type. Without this, an off-current-offer payment (e.g. the
                          // Alt Deal Structure slideout populating LEASE while the deal is on Finance)
                          // would store term records under finance-context ids that the lease grid
                          // can't resolve. For the main grid offerType === current offer (no change).
                          offerType,
                          getPairs,
                          extension: { DOWNPAYMENT: termIdParams.downpayment, FORDFLEXBUY: termIdParams.fordflexbuy }
                      })!
                : undefined;

        newOffer.terms = mapTermsForOffer(cloneDeep(offerPayments), offerType, autoTermId, {
            sourceApplication,
            isDueAtSigninAmountDeduct: getIsDueAtSigninAmountDeduct(state.dealer)
        });
        newOffer.incentives = incentiveUtils.mapIncentivesForOffer(cloneDeep(offer));
    }

    newOffer.traceId = result?.traceId;
    newOffer.actionForPayment = result?.actionForPayment;

    return newOffer;
};

export type DispatchPaymentReceivedParams = {
    dispatch: AnyDispatch;
    newOffer: TransformedOfferPaymentData;
    result: any;
    state: any;
    action: PaymentReduxAction;
    offerType: OfferType;
    includeIncentives?: boolean;
    standardsSeededByService?: boolean;
    dealerStandardsExcluded?: boolean;
    /**
     * The calculation endpoint this response's request was built for. Carried on the action because the
     * reducers cannot ask state: the PQ/UPQ switch can land between request and response.
     */
    requestCalculationEndpoint?: string;
};

/**
 * DE785177: `state` here is the one the request was built from, so the ledger total read from it is what the grid
 * row was priced on. Stamped only for grid rows, the one reply the reducer checks against it.
 */
const getRequestLedgerRebatesMeta = (state: any, action: PaymentReduxAction, offerType: OfferType) => {
    const rows = getOfferDetails(state, offerType).manualIncentives?.incentives;
    return isGridRebateIdsConsistencyEnabled(state) && isRegularGridRowAction(action) && rows
        ? { [REQUEST_LEDGER_REBATES_META]: sumLedgerRebates(rows) }
        : undefined;
};

export const dispatchPaymentReceived = ({
    dispatch,
    newOffer,
    result,
    state,
    action,
    offerType,
    includeIncentives,
    standardsSeededByService,
    dealerStandardsExcluded,
    requestCalculationEndpoint
}: DispatchPaymentReceivedParams): void => {
    const rawProgramIncentivesMeta = buildRawProgramIncentivesMeta(result, state, action, offerType, newOffer);

    dispatch(
        withMeta(
            {
                type: actionTypes.PAYMENT_RECEIVED,
                payload: newOffer,
                meta: {
                    includeIncentives,
                    ...(requestCalculationEndpoint && { requestCalculationEndpoint }),
                    ...(standardsSeededByService && { includeStandardIncentives: true }),
                    ...(dealerStandardsExcluded && { dealerStandardsExcluded: true }),
                    ...getRequestLedgerRebatesMeta(state, action, offerType),
                    // Carried here because alternate scenario scopes have featureToggles stripped (DE749297).
                    // Omitted when unset, since a hard `false` would pin the reducers' offer-node fallback shut.
                    ...(state.featureToggles?.enableEquityAllocationOrder !== undefined && {
                        enableEquityAllocationOrder: Boolean(state.featureToggles.enableEquityAllocationOrder)
                    }),
                    // Resolved here: the reducers only get the offer node, and getIsSalesView needs `dealer`
                    // and `widgetSettings` off the state tree (DE766119). Omitted when false to keep the fallback.
                    ...(getIsSalesView(state) && { isSalesViewStartingCash: true }),
                    ...rawProgramIncentivesMeta
                }
            },
            action,
            state
        )
    );
};

const fetchWithInjectedFetcher = async (
    paymentFetcher: NonNullable<IPaymentConfig['fetchPayments']>,
    paymentUrl: string,
    payload: Parameters<typeof fetchPayments>[1],
    action: PaymentReduxAction
): Promise<FetchPaymentResult> => {
    try {
        const outcome = await paymentFetcher(paymentUrl, payload, action.meta?.abortSignal, { action });
        if (outcome?.result || outcome?.error) {
            return outcome;
        }
        return { error: { message: 'Injected payment fetcher returned neither a result nor an error' } };
    } catch (error) {
        return toFetchPaymentError(error);
    }
};

/** Thunk action creator
 * Calls Payment Orchestrator, then dispatches various types of actions with the results
 *
 * TODO/Enhancement - transforms should be done in the reducer, not the middleware or action creator
 */
export const calculatePayments =
    (action: PaymentReduxAction, options: IPaymentConfig) =>
    async (dispatch: AnyDispatch, getState: () => any): Promise<void> => {
        const { selectors, paymentUrl, sourceApplication } = options;
        const state = getState();
        const useFetchBasedSpinner = getFeatureToggle(state, 'useFetchBasedSpinnerMV');
        const isNegativeLeaseQuoteRejectionEnabled = getFeatureToggle(state, 'DE783159_Enable_Negative_Lease_Quote_Rejection');
        const correlationId = selectors?.getCorrelationId?.(state) || '';
        const REQUEST_HEADERS: Record<string, string> = { 'Content-Type': 'application/json' };

        // TODO: Tried with Type 'OfferType' but caused issues with existing code
        const offerType: OfferType = getActionMetaValue(action, 'middleware')?.offerType || getCurrentOfferType(state);

        if (correlationId) {
            REQUEST_HEADERS[CORRELATION_ID_HEADER] = correlationId;
        }

        if (sourceApplication) {
            setActionMetaValue(action, sourceApplication, 'sourceApplication');
        }

        const payload = createPaymentPayload(state, selectors as HostSelectors, action, offerType);
        const includeIncentives = payload.payment?.includeIncentives;
        const isGridRowListOnly = getActionMetaValue(action, GRID_ASK_TURNED_ON_INCENTIVES_META) === true;
        const standardsSeededByService = didServiceSeedStandards(payload, offerType);
        const dealerStandardsExcluded = didRequestExcludeDealerStandards(payload, state, offerType, selectors as HostSelectors);
        const traceId = getTraceId(state);
        const actionForPayment = payload.metadata?.actionForPayment || getActionForPayment(state);

        const hasNoTradeInValue = !(payload.tradeIn?.value && payload.tradeIn.value > 0);
        const hasNoTradeInOwed = !(payload.tradeIn?.owed && payload.tradeIn.owed > 0);

        if (hasNoTradeInValue && hasNoTradeInOwed) {
            delete payload.tradeIn;
        }

        if (useFetchBasedSpinner) {
            dispatch(withMeta({ type: actionTypes.PAYMENT_FETCH_STARTED }, action));
        }

        const { result, error: fetchError } = options.fetchPayments
            ? await fetchWithInjectedFetcher(options.fetchPayments, paymentUrl, payload, action)
            : await fetchPayments(paymentUrl, payload, action.meta?.abortSignal);
        const error =
            fetchError ?? (isNegativeLeaseQuoteRejectionEnabled ? getNegativeLeaseQuoteError(result, offerType) : undefined);

        if (useFetchBasedSpinner) {
            dispatch(withMeta({ type: actionTypes.PAYMENT_FETCH_COMPLETED }, action));
        }

        if (error) {
            if (isRequestAborted(error.message)) {
                dispatch(
                    withMeta(
                        {
                            type: actionTypes.PAYMENT_ABORTED,
                            payload: {
                                error: error.message
                            }
                        },
                        action
                    )
                );
                return;
            }

            dispatch(paymentFailed(action));

            let meta;

            if (action.type !== 'FETCH_QUESTIONS_OR_PAYMENT') {
                if (selectors?.shouldKeepPaymentsOnFailure?.()) {
                    meta = {
                        shouldKeepPaymentsOnFailure: true
                    };
                }
            }
            dispatch(
                withMeta(
                    {
                        type: actionTypes.PAYMENT_FAILURE,
                        payload: errorPayment,
                        meta: {
                            ...(meta ? { meta } : undefined),
                            error,
                            shopperUpdateFailed:
                                action.type === 'UPDATED_SHOPPER_CONTACT' &&
                                !getActionMetaValue(action, EXPLICIT_PROGRAM_LOCK_META),
                            traceId,
                            actionForPayment,
                            offerType,
                            includeIncentives
                        }
                    },
                    action
                )
            );
            return;
        }

        const newOffer = buildOfferFromPaymentResult({ result, state, action, offerType, sourceApplication });

        dispatchPaymentReceived({
            dispatch,
            newOffer,
            result,
            state,
            action,
            offerType,
            includeIncentives: isGridRowListOnly ? false : includeIncentives,
            standardsSeededByService,
            dealerStandardsExcluded,
            requestCalculationEndpoint: resolveRequestCalculationEndpointFromAction(action)
        });

        return;
    };

export type ApplyStoredPaymentGridCellOptions = {
    offerType: OfferType;
    action: AnyFSA;
    sourceApplication?: string;
    includeIncentives?: boolean;
};

const isUsableStoredPaymentGridCell = (storedPo: any, offerType?: OfferType): boolean =>
    Boolean(offerType) && storedPo != null && typeof storedPo === 'object' && storedPo[offerType as string] != null;

export const applyStoredPaymentGridCell =
    (storedPo: Record<string, any> | null | undefined, options: ApplyStoredPaymentGridCellOptions) =>
    (dispatch: AnyDispatch, getState: () => any): boolean => {
        const { offerType, action, sourceApplication, includeIncentives } = options;

        if (!action || !isUsableStoredPaymentGridCell(storedPo, offerType)) {
            return false;
        }

        const state = getState();
        const paymentAction = action as PaymentReduxAction;

        let newOffer: TransformedOfferPaymentData;
        try {
            newOffer = buildOfferFromPaymentResult({
                result: storedPo,
                state,
                action: paymentAction,
                offerType,
                sourceApplication
            });
        } catch {
            return false;
        }

        dispatchPaymentReceived({
            dispatch,
            newOffer,
            result: storedPo,
            state,
            action: paymentAction,
            offerType,
            includeIncentives
        });

        return true;
    };

export const fakePaymentFailure =
    (action: AnyFSA) =>
    (dispatch: AnyDispatch, getState: () => any): void => {
        dispatch(paymentFailed(action));

        // Simulate a failure that takes 7 seconds to process server-side
        setTimeout(() => {
            dispatch(
                withMeta(
                    {
                        type: actionTypes.PAYMENT_FAILURE,
                        payload: {
                            finance: {
                                success: false,
                                incentives: [],
                                selectedTermMonths: null
                            },
                            lease: {
                                success: false,
                                incentives: [],
                                selectedTermMonths: null
                            },
                            terms: getDefaultTerms(),
                            incentives: {
                                allIds: [],
                                byId: [],
                                byOfferType: [],
                                mappings: []
                            },
                            zipCodePaymentFailed: false
                        },
                        // NOTE: Technically this violates FSA rules - only type, payload and meta are allowed
                        msg: 'Invalid payment object, please view docs for info.'
                        // Force it to be "allowed" by casting as AnyFSA
                    } as AnyFSA,
                    action,
                    getState()
                )
            );
        }, PAYMENT_FAILURE_TIMEOUT);
    };

export const applyDealerOverrides = (
    paymentRequestOverride?: IPaymentOverride,
    withSubmit: boolean = false,
    offerType?: OfferType,
    metaOptions?: { includeStandardIncentives?: boolean }
): PaymentReduxAction => ({
    type: APPLY_DEALER_PAYMENT_OVERRIDES,
    meta: {
        middleware: {
            payment: true,
            offerType
        },
        withSubmit,
        paymentRequestOverride,
        ...(metaOptions?.includeStandardIncentives && { includeStandardIncentives: true })
    }
});

export const updateDaysToFirstPayment = (daysToFirstPayment: number): PaymentReduxAction => {
    return {
        type: actionTypes.UPDATED_DAYS_TO_FIRST_PAYMENT,
        payload: daysToFirstPayment,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
};
export const updateContractDate = (contractDate: string | undefined, makePaymentCall: boolean = false): PaymentReduxAction => {
    return {
        type: actionTypes.UPDATE_CONTRACT_DATE,
        payload: contractDate,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const updateUseCalculatedGovFees = ({
    useCalculatedGovFees,
    makePaymentCall = true
}: {
    useCalculatedGovFees: boolean;
    makePaymentCall?: boolean;
}): PaymentReduxAction => {
    return {
        type: actionTypes.UPDATED_USE_CALCULATED_GOV_FEES,
        payload: useCalculatedGovFees,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const updateSpecialPaymentFormula = (formula: SpecialPaymentFormula.FordFlexBuy): PaymentReduxAction => {
    return {
        type: actionTypes.UPDATED_SPECIAL_PAYMENT_FORMULA,
        payload: formula,
        meta: {
            middleware: {
                // initiate payment call request handled in updatedOfferType action
                payment: false
            }
        }
    };
};

export const updatePaymentFrequency = (
    offerType: OfferType,
    frequency: PaymentScheduleTypes,
    downPayment?: number | null,
    options?: {
        skipProgramOverride?: boolean;
    }
): PaymentReduxAction => {
    const { skipProgramOverride } = options || {};

    return {
        type: actionTypes.UPDATED_PAYMENT_FREQUENCY,
        payload: { offerType, frequency },
        meta: {
            middleware: {
                payment: true,
                ...(skipProgramOverride && { skipProgramOverride: true })
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                ...(downPayment !== undefined && {
                    [offerType]: { downPayment }
                })
            }
        }
    };
};

export const resetSpecialPaymentFormula = () => {
    return {
        type: actionTypes.RESET_SPECIAL_PAYMENT_FORMULA,
        meta: {
            middleware: {
                // initiate payment call request handled in updatedOfferType action
                payment: false
            }
        }
    };
};

// TODO: offerPayments can be from any offer type 'finance' 'lease' or 'cash', so need to have 'any' type only
function populateFlexBuyPaymentData(params: {
    state: any;
    currentOfferType: OfferType;
    offerPayments: GenericObject[];
    newOffer: TransformedOfferPaymentData;
    action: PaymentReduxAction;
}): TransformedOfferPaymentData | void {
    const { state, currentOfferType, offerPayments, newOffer, action } = params;
    const isFlexBuy = isFlexBuyPayment(state);

    if (currentOfferType.toLowerCase() !== FINANCE || !isFlexBuy) {
        return;
    }

    const currentDownPayment = getDownPayment(state);
    const currentTermMonth = getSelectedTermMonths(state) as number;
    const currentInitialPaymentDiscount = getOfferInitialPaymentDiscount(state)[0];

    const foundOfferPayment =
        currentInitialPaymentDiscount != null
            ? offerPayments.find(
                  (payment) =>
                      payment.netCashDown === currentDownPayment &&
                      payment.term.months === currentTermMonth &&
                      payment.initialPaymentDiscount === currentInitialPaymentDiscount
              ) || offerPayments[0]
            : offerPayments[0] || {};

    const { initialPaymentDiscount, comparableTermPaymentDetail, firstTermPaymentDetail, secondTermPaymentDetail } =
        foundOfferPayment;

    const flexBuyPayment = {
        initialPaymentDiscount,
        comparableTermPaymentDetail,
        firstTermPaymentDetail,
        secondTermPaymentDetail
    };

    newOffer[FINANCE]!.flexBuyPayment = flexBuyPayment;

    const isFlexBuyPaymentGridEnabled = getFlexBuyPaymentGridToggle(state);

    const paymentRequestOverride = action.meta?.paymentRequestOverride?.[FINANCE] || {};

    const { flexBuyDownPayments, flexBuyPercents, flexBuyTerms } = paymentRequestOverride;

    if (isFlexBuyPaymentGridEnabled && flexBuyDownPayments?.length && flexBuyPercents?.length && flexBuyTerms?.length) {
        const flexBuyPaymentGridData: UpdateFlexBuyPaymentGridDataPayload = flexBuyDownPayments.map((dp, index) => ({
            key: index,
            value: dp,
            items: flexBuyTerms.map((term, colIdx): FlexBuyPaymentGridDataItem => {
                const foundPercentByColId = flexBuyPercents[colIdx];
                const foundOfferPayment = offerPayments.find(
                    (payment) =>
                        payment.netCashDown === dp &&
                        payment.term?.months === term &&
                        payment.initialPaymentDiscount === foundPercentByColId
                );

                const id = `${foundOfferPayment?.netCashDown ?? 'empty'}-${term}-${foundPercentByColId}-${index}-${colIdx}`;

                const foundTerm = foundOfferPayment?.term?.months || term;

                return {
                    id,
                    term: foundTerm,
                    downPayment: foundOfferPayment?.netCashDown,
                    initialPaymentDiscount: foundOfferPayment?.initialPaymentDiscount,
                    firstTermPaymentDetail: foundOfferPayment?.firstTermPaymentDetail,
                    secondTermPaymentDetail: foundOfferPayment?.secondTermPaymentDetail,
                    comparableTermPaymentDetail: foundOfferPayment?.comparableTermPaymentDetail,
                    ...(foundOfferPayment?.totalOfPaymentsAndConsideration != null && {
                        totalOfPaymentsAndConsideration: foundOfferPayment.totalOfPaymentsAndConsideration
                    }),
                    isSelected:
                        dp === currentDownPayment &&
                        foundTerm === currentTermMonth &&
                        foundOfferPayment?.initialPaymentDiscount === initialPaymentDiscount,
                    isEmpty:
                        foundOfferPayment?.firstTermPaymentDetail?.paymentAmount == null &&
                        foundOfferPayment?.secondTermPaymentDetail?.paymentAmount == null,
                    isLoading: false
                };
            })
        }));
        newOffer[FINANCE]!.flexBuyPaymentGridData = flexBuyPaymentGridData;
        newOffer[FINANCE]!.selectedTermMonths = currentTermMonth;
    }
}
